import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";
import { toEmbedUrl } from "../embedUtils";

export const schema = globalSectionSchema.extend({
  heading: z.string().optional(),
  subheading: z.string().optional(),
  videoUrl: z.string().default("about:blank"),
  aspectRatio: z.enum(["16:9", "4:3", "1:1"]).default("16:9"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

const aspectClasses: Record<string, string> = {
  "16:9": "aspect-video",
  "4:3": "aspect-[4/3]",
  "1:1": "aspect-square",
};

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const embedSrc = toEmbedUrl(safe.videoUrl);
  const aspect = aspectClasses[safe.aspectRatio] || aspectClasses["16:9"];

  return (
    <SectionShell globals={safe} bare>
      <div className="max-w-4xl mx-auto px-6">
        {(safe.heading || safe.subheading) && (
          <div className="text-center mb-8">
            {safe.heading && (
              <RichText
                as={safe.headingLevel}
                html={safe.heading}
                className="text-3xl @md:text-[2.5rem] font-bold mb-4"
                data-prop="heading"
              />
            )}
            {safe.subheading && (
              <RichText
                as="div"
                html={safe.subheading}
                className="text-lg opacity-80"
                data-prop="subheading"
              />
            )}
          </div>
        )}

        <div className={`${aspect} w-full rounded-lg overflow-hidden bg-surface`}>
          <iframe
            src={embedSrc}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            title={safe.heading || "Video"}
          />
        </div>
      </div>
    </SectionShell>
  );
}
